import type { CurrencyType } from '@dealstack/prisma-client';
import type { DataSecurityTct } from '@dealstack/shared/lib/generated';
import { isNumber } from 'lodash';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';

export const formatNumberToUI = (
	number: number,
	currency?: CurrencyType | string,
	precision = 0
): string => {
	let precisionValue = currency ? 2 : precision;

	// Cap the precision to 5 decimal places to avoid breaking the UI if a much higher value is passed
	if (precisionValue > 5) {
		precisionValue = 5;
	}

	const { format } = new Intl.NumberFormat('en-IE', {
		minimumFractionDigits: precisionValue,
		maximumFractionDigits: precisionValue,
	});

	if (currency) {
		return `${currency} ${format(number)}`;
	}

	return format(number);
};

export const maybeAddZeroBeforeNumber = (number: number): string | number =>
	number < 10 ? `0${number}` : number;

export const numberToLetter = (number: number): string => (number + 9).toString(36).toUpperCase();

export const convertNumber = (value?: string) =>
	!isNaN(Number(value)) && isFinite(Number(value)) ? Number(value) : undefined;

const compactNumberFormat = new Intl.NumberFormat(undefined, {
	notation: 'compact',
	compactDisplay: 'short',
});

export const numberToCompact = (number: number | null): string =>
	typeof number === 'number' ? compactNumberFormat.format(number) : '-';

export const getPercentage = (value: number | null, totalValue: number | null): number => {
	if (value === null || totalValue === null) {
		return 0;
	}
	if (value === 0 || totalValue === 0) {
		return 0;
	}
	return (value / totalValue) * 100;
};

export const numberToUiPercent = (
	percent?: number,
	allowZero?: boolean,
	fractionDigits = 2
): string | undefined => {
	if (!percent && !allowZero) {
		return;
	}

	return `${(percent || 0).toFixed(fractionDigits)}%`;
};

export const extractDecimalPrecisionFromUnitDenominator = (
	unitDenominator?: DataSecurityTct['unitDenominator'] | null
): number => {
	return Math.log10(unitDenominator || 1);
};

export const isFiniteNumber = (value: unknown): boolean => {
	return isNumber(value) && Number.isFinite(value);
};

/** A plain hyphen, used where a value is absent rather than zero. */
const HYPHEN_MINUS = '-';

/**
 * How a negative renders. The UI's long-standing convention is accounting parentheses; a
 * document needs a minus sign, because `(2,500.00)` in an executed deed reads as a footnote.
 */
export type NegativeNumberStyle = 'parentheses' | 'minus';

/**
 * Decimal places in `number`.
 *
 * Exponential notation is handled explicitly: `toString()` switches to it below 1e-6, so
 * reading the digits after the dot alone counted `1e-7` as having NONE and rounded a nominal
 * value of 0.0000001 away to `0.00`.
 */
const countDecimals = (number: number): number => {
	if (Math.floor(number) === number) {
		return 0;
	}
	const [mantissa = '', exponent] = number.toString().split('e-');
	const mantissaDecimals = mantissa.split('.')[1]?.length ?? 0;
	return exponent === undefined ? mantissaDecimals : mantissaDecimals + Number(exponent);
};

/**
 * The platform's number renderer, shared by the cap-table UI and by generated documents.
 *
 * `minPrecision`/`maxPrecision` are a RANGE: trailing zeros are only padded when
 * `lockPrecision` is set, so a unit count can hold five decimal places without turning `1000`
 * into `1,000.00000`. `negativeStyle` and `returnNumericalZero` carry the two conventions the
 * two callers genuinely differ on — keep them explicit at every document call site, since the
 * defaults here are the UI's.
 */
export const formatNumber = ({
	value,
	currency,
	lockPrecision,
	minPrecision = 2,
	maxPrecision = minPrecision,
	returnNumericalZero = false,
	currencyDisplay = 'code',
	negativeStyle = 'parentheses',
	locale = 'en-US',
}: {
	value: number;
	currency?: string;
	lockPrecision?: boolean;
	minPrecision?: number;
	maxPrecision?: number;
	returnNumericalZero?: boolean;
	currencyDisplay?: keyof Intl.NumberFormatOptionsCurrencyDisplayRegistry;
	negativeStyle?: NegativeNumberStyle;
	locale?: string;
}): string => {
	if (value === 0 && !returnNumericalZero) {
		return HYPHEN_MINUS;
	}
	// `-0 < 0` is false, so a negated zero renders as plain zero rather than a signed one.
	const isNegative = value < 0;
	const absValue = Math.abs(value);
	const hasDecimal = absValue % 1 !== 0;
	const decimalsCount = countDecimals(absValue);
	const minPrecisionValue = lockPrecision ? minPrecision : 0;
	const maxPrecisionValue = ((): number => {
		if (!hasDecimal) {
			return minPrecisionValue;
		}
		if (decimalsCount < minPrecisionValue) {
			return minPrecisionValue;
		}
		if (maxPrecision == null) {
			return decimalsCount;
		}
		return decimalsCount < maxPrecision ? decimalsCount : maxPrecision;
	})();

	const absValueFixed = absValue.toLocaleString(locale, {
		maximumFractionDigits: maxPrecisionValue,
		minimumFractionDigits: minPrecisionValue,
		...(currency ? { style: 'currency', currency, currencyDisplay } : {}),
	});

	if (!isNegative) {
		return absValueFixed;
	}
	return negativeStyle === 'minus' ? `-${absValueFixed}` : `(${absValueFixed})`;
};

export function addCommasToNumberString(numStr: string): string {
	// Split into integer and decimal parts
	const [intPart = '', decimalPart] = numStr.split('.');
	const decimalPartProcessed = decimalPart !== undefined ? `.${decimalPart}` : '';

	// Build array from right to left, then reverse and join
	const chars: string[] = [];
	for (const character of [...intPart].reverse()) {
		if (chars.length > 0 && chars.length % 4 === 3) {
			chars.push(',');
		}
		chars.push(character);
	}

	return chars.reverse().join('') + decimalPartProcessed;
}

export const formatDecimal = ({
	value,
	lockPrecision,
	minPrecision = 2,
	maxPrecision = minPrecision,
	returnNumericalZero = false,
}: {
	value: HighPrecisionDecimal;
	lockPrecision?: boolean;
	minPrecision?: number;
	maxPrecision?: number;
	returnNumericalZero?: boolean;
}): string => {
	if (value.isZero() && !returnNumericalZero) {
		return HYPHEN_MINUS;
	}
	const isNegative = value.isNegative();
	const absValue = value.abs();

	// If lockPrecision is true, we clamp the dp between minPrecision and maxPrecision.
	// Any DP that goes out of bounds is replaced with the closest bound.
	// Otherwise, the dp is bounded by maxPrecision only.
	const dp = lockPrecision
		? Math.min(Math.max(minPrecision, absValue.dp()), maxPrecision)
		: Math.min(absValue.dp(), maxPrecision);
	const absValueFixed = absValue.toFixed(dp);

	// Add commas to the formatted number
	const absValueFixedWithCommas = addCommasToNumberString(absValueFixed);

	return isNegative ? `(${absValueFixedWithCommas})` : absValueFixedWithCommas;
};

export const formatValueWithUnitDenominator = (
	value: number | null,
	unitDenominator: number | null,
	returnNumericalZero = false
): string => {
	const securityPrecision = extractDecimalPrecisionFromUnitDenominator(unitDenominator);
	return formatNumber({
		value: value ?? 0,
		lockPrecision: securityPrecision > 1,
		minPrecision: securityPrecision,
		maxPrecision: securityPrecision,
		returnNumericalZero,
	});
};
